import { FC, useMemo, useCallback, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import { FilterChip } from "@/components/filters/FilterChip";
import { useFilterDraft } from "@/hooks/filters";
import { useTopicLabel } from "@/lib/company-filter/modules/intent/hooks";
import { useBulkTopicOperations } from "@/lib/company-filter/modules/intent/hooks/useBulkTopicOperations";
import { TopicUploadDialog } from "@/lib/company-filter/modules/intent/components/TopicUploadDialog";
import { MAX_INTENT_TOPICS } from "@/lib/company-filter/modules/intent/slice";
import { useSnackbar } from "@/components/Snackbar";
import { useIntentFiltersStorage } from "../hooks/useIntentFiltersStorage";
import { IntentTopicSelector } from "@/components/filters/IntentTopicSelector";
import type { CachedTopic } from "@/types/intent";

type TopicDraftValue = { ids: string[]; cache: CachedTopic[] };

export const TopicFilter: FC = () => {
  const { intentFilters, setIntentFilters } = useIntentFiltersStorage();
  const { showWarning } = useSnackbar();
  const { handleDownload } = useBulkTopicOperations();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);

  const initialValue = useMemo<TopicDraftValue>(
    () => ({ ids: intentFilters.topicIds, cache: intentFilters.topicsCache }),
    [intentFilters.topicIds, intentFilters.topicsCache]
  );

  const topicsDraft = useFilterDraft(
    initialValue,
    (value) =>
      setIntentFilters((prev) => ({
        ...prev,
        topicIds: value.ids,
        topicsCache: value.cache,
      })),
    {
      getEmptyValue: () => ({ ids: [], cache: [] }),
      isEqual: (a, b) =>
        a.ids.length === b.ids.length &&
        a.ids.every((id, i) => id === b.ids[i]),
    }
  );

  const selectedTopicsCache = useMemo(() => {
    const cacheById = new Map<string, CachedTopic>();
    intentFilters.topicsCache.forEach((t) => cacheById.set(t.id, t));
    topicsDraft.draft.cache.forEach((t) => cacheById.set(t.id, t));

    return topicsDraft.draft.ids
      .map((id) => cacheById.get(id))
      .filter((t): t is CachedTopic => !!t);
  }, [
    intentFilters.topicsCache,
    topicsDraft.draft.cache,
    topicsDraft.draft.ids,
  ]);

  const handleClear = useCallback(() => {
    setIntentFilters((prev) => ({
      ...prev,
      topicIds: [],
      topicsCache: [],
    }));
  }, [setIntentFilters]);

  const count = intentFilters.topicIds.length;
  const label = useTopicLabel(
    intentFilters.topicIds,
    intentFilters.topicsCache
  );

  const getSelectedCount = useCallback(() => count, [count]);

  const popoverOptions = useMemo(
    () => ({
      onClearButtonClick: topicsDraft.clearDraft,
      onCancelButtonClick: topicsDraft.cancelChanges,
      onApplyButtonClick: topicsDraft.applyChanges,
      disableApplyButton: !topicsDraft.hasChanges,
      disableCancelButton: false,
    }),
    [topicsDraft]
  );

  const handleUploadSuccess = useCallback(
    (uploadedTopics: CachedTopic[]) => {
      const existingIds = new Set(topicsDraft.draft.ids);
      const newTopics = uploadedTopics.filter(
        (topic) => !existingIds.has(topic.id)
      );

      if (newTopics.length === 0) return;

      const remainingSlots = MAX_INTENT_TOPICS - topicsDraft.draft.ids.length;
      if (remainingSlots <= 0) {
        showWarning(
          `You can select up to ${MAX_INTENT_TOPICS.toLocaleString()} intent topics. Remove some topics to add more.`
        );
        return;
      }

      const topicsToAdd = newTopics.slice(0, remainingSlots);
      if (topicsToAdd.length < newTopics.length) {
        showWarning(
          `Only ${topicsToAdd.length.toLocaleString()} of ${newTopics.length.toLocaleString()} topics were added. Intent topics are limited to ${MAX_INTENT_TOPICS.toLocaleString()}.`
        );
      }

      topicsDraft.updateDraft({
        ids: [...topicsDraft.draft.ids, ...topicsToAdd.map((t) => t.id)],
        cache: [...topicsDraft.draft.cache, ...topicsToAdd],
      });
    },
    [topicsDraft, showWarning]
  );

  const handleDownloadClick = useCallback(() => {
    handleDownload(selectedTopicsCache);
  }, [handleDownload, selectedTopicsCache]);

  const titleActions = useMemo(
    () => (
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="Upload topics from CSV">
          <IconButton size="small" onClick={() => setIsUploadDialogOpen(true)}>
            <UploadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            topicsDraft.draft.ids.length === 0
              ? "Select topics to download"
              : "Download selected topics"
          }
        >
          <span>
            <IconButton
              size="small"
              disabled={topicsDraft.draft.ids.length === 0}
              onClick={handleDownloadClick}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    ),
    [topicsDraft.draft.ids.length, handleDownloadClick]
  );

  return (
    <>
      <FilterChip
        label="Topic"
        getSelectedCount={getSelectedCount}
        onDelete={count > 0 ? handleClear : undefined}
        renderLabel={() => (
          <Stack flexDirection="row" gap="3px">
            <Typography color="secondary">Topic:</Typography>
            <Typography>{label}</Typography>
          </Stack>
        )}
        paperStyles={{ width: 500 }}
        popoverOptions={popoverOptions}
        titleActions={titleActions}
      >
        <Box id="intent-topic-filter" mb={1}>
          <IntentTopicSelector
            selectedTopicIds={topicsDraft.draft.ids}
            topicsCache={selectedTopicsCache}
            onSelectionChange={(ids, cache) =>
              topicsDraft.updateDraft({ ids, cache })
            }
            showUploadDownload={false}
            mx={0}
          />
        </Box>
      </FilterChip>

      <TopicUploadDialog
        open={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        onSuccess={handleUploadSuccess}
      />
    </>
  );
};
